/**
 * TurnNotices — how a turn ended, as chrome under the answer.
 *
 * The Phase 0/3 controls (the per-turn token ceiling, the event cap, the wall
 * clock, the per-tool timeout, the repeat breaker) used to be visible to the
 * user only as whatever prose survived into the answer — an italic sentence at
 * the bottom, or nothing when the model absorbed the advice silently. A
 * counselor reading "You have 4 open prospects" could not tell a finished
 * answer from one cut short at a cap.
 *
 * This renders the server's structured `outcome.notices` as `SkyMessageBar`s,
 * the way SkyChat's rail renders a stopped turn as a marker beside the text
 * rather than inside it. It is IN ADDITION to the answer text: the appended
 * sentence the model and older clients see is left exactly as it was.
 *
 * The words live in `turnNoticeCopy.ts`, a UI-free module, so a server test
 * can hold them against the server's own appended sentences.
 */
import { SkyMessageBar } from "@skypointcloud/skypoint-ui";
import type { TurnNotice } from "@/store/api/crmSkyAgentApi";
import { TURN_NOTICE_COPY } from "./turnNoticeCopy";

export interface TurnNoticesProps {
  notices: ReadonlyArray<TurnNotice>;
  /**
   * Leave out the `error` kind. The chat hook already surfaces a terminal error
   * chunk as its own error bar with the server's friendly message, so while
   * that bar is on screen a second red bar for the same event would be noise.
   */
  hideErrorKind?: boolean;
}

export function TurnNotices({ notices, hideErrorKind = false }: TurnNoticesProps) {
  const shown = notices.filter((n) => !(hideErrorKind && n.kind === "error"));
  if (shown.length === 0) return null;
  return (
    <div
      className="mt-2 space-y-2"
      data-testid="turn-notices"
    >
      {shown.map((n, i) => {
        const { intent, text } = TURN_NOTICE_COPY[n.kind](n);
        return (
          <SkyMessageBar
            key={`${n.kind}-${n.tool ?? ""}-${i}`}
            intent={intent}
            message={text}
            layout="multiline"
            testId={`turn-notice-${n.kind}`}
          />
        );
      })}
    </div>
  );
}
